import type { OverviewStats, RecentJob, RecentRunGroup } from '../services/api';

/**
 * Pure helpers for the Dashboard "Recent Activity" table's run-grouping.
 *
 * A Broker Doc Assist chat produces one job per turn (the upload that opens it
 * + each follow-up question), all sharing a conversationId. The backend
 * collapses them into `recentRunGroups`; this module resolves the table's rows
 * from that (or synthesizes singletons from the flat `recentJobs` when an older
 * backend doesn't send groups) and patches optimistic state across both shapes.
 */

/** Wrap a single run as a non-conversation group (renders as a normal row). */
export function singletonGroup(job: RecentJob): RecentRunGroup {
  return { groupId: job.jobId, isConversation: false, parent: job, turns: [job], turnCount: 1 };
}

/**
 * Resolve the table's groups + the pagination total. Prefers the backend's
 * conversation-grouped `recentRunGroups`; falls back to one singleton group per
 * flat `recentJobs` row when the field is absent (older backend / legacy tests).
 */
export function toRunGroups(
  stats: OverviewStats | null | undefined,
): { groups: RecentRunGroup[]; total: number } {
  if (stats?.recentRunGroups) {
    return {
      groups: stats.recentRunGroups,
      total: stats.totalRecentRunGroups ?? stats.recentRunGroups.length,
    };
  }
  const jobs = stats?.recentJobs ?? [];
  return { groups: jobs.map(singletonGroup), total: stats?.totalRecentJobs ?? jobs.length };
}

/**
 * Apply a partial patch to the run with `jobId` everywhere it appears in the
 * stats — the flat `recentJobs` AND every group's parent + turns — so an
 * optimistic update (e.g. failure-notified) reflects regardless of which shape
 * the table renders from. A conversation PARENT is synthetic and shares the
 * chat's id, so it's only patched when its own jobId matches (it won't for a
 * per-turn failure action, which targets a turn's jobId).
 */
export function patchJobInStats(
  prev: OverviewStats | null,
  jobId: string,
  patch: Partial<RecentJob>,
): OverviewStats | null {
  if (!prev) return prev;
  const patchRow = (r: RecentJob): RecentJob => (r.jobId === jobId ? { ...r, ...patch } : r);
  return {
    ...prev,
    recentJobs: prev.recentJobs?.map(patchRow),
    recentRunGroups: prev.recentRunGroups?.map((g) => ({
      ...g,
      parent: patchRow(g.parent),
      turns: g.turns.map(patchRow),
    })),
  };
}
